import type { i18n } from "i18next";
import { LOCALES, NAMESPACES } from "./constants.js";
import { sensorNamingCatalogs } from "./sensorNamingCatalogData.js";
import { warningCatalogs } from "./warningCatalogData.js";
import { notificationCatalogs } from "./notificationCatalogData.js";
import { thirdPartyApiCatalogs } from "./thirdPartyApiCatalogData.js";
import { timezoneCatalogs } from "./timezoneCatalogData.js";
import { formatHoursAndMinutes, formatHoursMinutesAndSeconds, formatRoundedHoursOrMinutes } from "../durationFormat.js";

/**
 * Registers warning catalogs + warning formatters on an i18next instance.
 * This exists so dashboard/storybook i18n setups can consume verdi-common warning
 * copy with the same formatter behavior used elsewhere.
 * Call once after `i18n.init(...)` resolves.
 */
export function registerWarningsTranslations(i18nInstance: i18n): void {
    for (const locale of LOCALES) {
        i18nInstance.addResourceBundle(locale, NAMESPACES.WARNINGS, warningCatalogs[locale], true, false);
    }
    i18nInstance.services.formatter?.add("roundedHoursOrMinutes", (value, lng) =>
        formatRoundedHoursOrMinutes(Number(value), lng ?? "en-CA"),
    );
    i18nInstance.services.formatter?.add("formatHoursAndMinutes", (value, lng) =>
        formatHoursAndMinutes(Number(value), lng ?? "en-CA"),
    );
    i18nInstance.services.formatter?.add("formatHoursMinutesAndSeconds", (value, lng) =>
        formatHoursMinutesAndSeconds(Number(value), lng ?? "en-CA"),
    );
}

/**
 * Registers notification copy catalogs on an i18next instance.
 * This exists so backend/services and dashboard/storybook setups compose SMS copy
 * from the same translator-controlled templates.
 * Call once after `i18n.init(...)` resolves.
 */
export function registerNotificationsTranslations(i18nInstance: i18n): void {
    LOCALES.forEach((locale) => {
        i18nInstance.addResourceBundle(locale, NAMESPACES.NOTIFICATIONS, notificationCatalogs[locale], true, false);
    });
}

/**
 * Registers the sensor-naming catalogs (unit category + sensor element display names) on an
 * i18next instance so dashboard/storybook setups can resolve them via `t("sensorNaming:...")`.
 * The direct getters in `sensorNamingCatalogData` don't require this; it exists for consumers
 * that prefer react-i18next. Call once after `i18n.init(...)` resolves.
 */
export function registerSensorNamingTranslations(i18nInstance: i18n): void {
    LOCALES.forEach((locale) => {
        i18nInstance.addResourceBundle(locale, NAMESPACES.SENSOR_NAMING, sensorNamingCatalogs[locale], true, false);
    });
}

/**
 * Registers the generated timezone city-name catalogs on an i18next instance, so consumers can read
 * `zone.<IANA id>` from the `timezones` namespace. Every locale is mounted up front — the data is
 * bundled, not fetched — which is what lets a consumer read the bundle synchronously instead of
 * waiting on a namespace load. Call once after `i18n.init(...)` resolves.
 */
export function registerTimezonesTranslations(i18nInstance: i18n): void {
    LOCALES.forEach((locale) => {
        i18nInstance.addResourceBundle(locale, NAMESPACES.TIMEZONES, timezoneCatalogs[locale], true, false);
    });
}

/**
 * Registers the third-party API catalogs so consumers can resolve `t("thirdPartyApi:...")`. The
 * direct getters in `thirdPartyApiCatalogData` don't need this; it exists for react-i18next users.
 * Call once after `i18n.init(...)` resolves.
 */
export function registerThirdPartyApiTranslations(i18nInstance: i18n): void {
    LOCALES.forEach((locale) => {
        i18nInstance.addResourceBundle(locale, NAMESPACES.THIRD_PARTY_API, thirdPartyApiCatalogs[locale], true, false);
    });
}
